import React from "react";
import SectionTitle from "../common/SectionTitle";
import SubHeading from "../common/SubHeading";
import { useFormSection, FormSectionProps } from "../../hooks/useFormSection";

const yesNoOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const maritalStatusOptions = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
];

const jobStatusOptions = [
  { value: "working", label: "Working" },
  { value: "unemployed", label: "Unemployed" },
  { value: "student", label: "Student" },
];

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const PersonalInfoSectionV2: React.FC<FormSectionProps> = (props) => {
  const { Field, formData } = useFormSection(props);
  
  // Access nested data safely
  const practitionerData = formData?.practitionerQuestions || {};

  return (
    <section>
      <SectionTitle>Client Intake & Assessment</SectionTitle>
      
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <Field
          path="personalInfo.fullName"
          label="Full Name"
          type="text"
          required
          placeholder="Jane Doe"
        />
        <Field
          path="personalInfo.date"
          label="Date"
          type="date"
          required
        />
        <Field
          path="personalInfo.phone"
          label="Phone Number"
          type="tel"
          placeholder="(123) 456-7890"
        />
      </div>
      
      {/* Address */}
      <div className="mt-4">
        <Field
          path="personalInfo.address"
          label="Home Address"
          type="text"
          placeholder="123 Wellness St, Healthville"
        />
      </div>
      
      {/* Status Information */}
      <div className="flex flex-wrap gap-x-8 gap-y-4 mt-4 bg-brand-stone-50 p-4 rounded-lg">
        <Field
          path="personalInfo.maritalStatus"
          label="Marital Status"
          type="select"
          options={maritalStatusOptions}
          placeholder="Select..."
        />
        <Field
          path="personalInfo.jobStatus"
          label="Job Status"
          type="select"
          options={jobStatusOptions}
          placeholder="Select..."
        />
      </div>
      
      {/* Physical Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 mt-4">
        <Field
          path="personalInfo.height"
          label="Height (cm/in)"
          type="text"
        />
        <Field
          path="personalInfo.weight"
          label="Weight (kg/lbs)"
          type="text"
        />
        <Field
          path="personalInfo.dob"
          label="Birth Date"
          type="date"
          required
        />
        <Field
          path="personalInfo.gender"
          label="Gender"
          type="select"
          options={genderOptions}
          placeholder="Select..."
        />
      </div>
      
      {/* Demographics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mt-4">
        <Field
          path="personalInfo.ethnicity"
          label="Ethnicity"
          type="text"
        />
        <Field
          path="personalInfo.birthPlace"
          label="Birth Place"
          type="text"
        />
      </div>
      
      {/* Emergency Contact */}
      <div className="mt-6 border-t pt-6">
        <SubHeading>Emergency Contact</SubHeading>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
          <Field
            path="personalInfo.emergencyContactName"
            label="Full Name"
            type="text"
          />
          <Field
            path="personalInfo.emergencyContactRelationship"
            label="Relationship"
            type="text"
          />
          <Field
            path="personalInfo.emergencyContactPhone"
            label="Phone Number"
            type="tel"
          />
        </div>
      </div>
      
      {/* Past Practitioner Questions */}
      <div className="mt-6 border-t pt-6">
        <SubHeading>Past Practitioner Questions</SubHeading>
        
        <Field
          path="practitionerQuestions.seenFunctionalHealthPro"
          label="1. Have you seen a functional / alternative health professional for your current or past health concerns?"
          type="radio"
          options={yesNoOptions}
        />
        
        {practitionerData.seenFunctionalHealthPro === "yes" && (
          <Field
            path="practitionerQuestions.functionalHealthProDetails"
            label="If yes, which, and did they help with the health concerns?"
            type="textarea"
            rows={3}
          />
        )}
        
        <Field
          path="practitionerQuestions.seenFamilyDoctor"
          label="2. Have you seen your family doctor / specialist for your current health concern?"
          type="radio"
          options={yesNoOptions}
        />
        
        {practitionerData.seenFamilyDoctor === "yes" && (
          <Field
            path="practitionerQuestions.familyDoctorDetails"
            label="If yes, which specialist and did they help with the current health concerns?"
            type="textarea"
            rows={3}
          />
        )}
      </div>
    </section>
  );
};

export default PersonalInfoSectionV2;